import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { resolveBackendUrl } from '../config/api.config';
import logger from '../utils/logger';

export type LogLevel = 'info' | 'success' | 'warning' | 'error';
export type LogCategory =
    | 'AUTH'
    | 'ACCOUNT'
    | 'ORDER'
    | 'SOCKET'
    | 'SYSTEM'
    | 'UI'
    | 'VALIDATION'
    | 'API';

export interface LogEntry {
    id: string;
    timestamp: Date;
    level: LogLevel;
    category: LogCategory;
    message: string;
    details?: any;
    metadata?: {
        userId?: string;
        sessionId?: string;
        action?: string;
    };
}

interface NotificationLogContextType {
    logs: LogEntry[];
    addLog: (level: LogLevel, category: LogCategory, message: string, details?: any, metadata?: any) => void;
    clearLogs: () => void;
    getLogsByCategory: (category: LogCategory) => LogEntry[];
    getLogsByLevel: (level: LogLevel) => LogEntry[];
    unreadCount: number;
    markAsRead: () => void;
}

const NotificationLogContext = createContext<NotificationLogContextType | undefined>(undefined);

// Maximum number of logs to keep in memory (for performance)
const MAX_LOGS = 1000;

// Log retention time (older logs will be removed)
const LOG_RETENTION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Backend API URL
const ACTIVITY_LOG_URL = resolveBackendUrl('/api/logs/save');

// TEMPORARILY DISABLED: Send log to backend for file storage
// Uncomment when testing performance impact
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sendLogToBackend = async (log: LogEntry): Promise<void> => {
    try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(ACTIVITY_LOG_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify({
                logs: [{
                    id: log.id,
                    timestamp: log.timestamp.toISOString(),
                    level: log.level,
                    category: log.category,
                    message: log.message,
                    details: log.details,
                    metadata: log.metadata
                }]
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.status !== 'success') {
            throw new Error(data.message || 'Failed to save log');
        }
    } catch (error) {
        // Silent fail - don't throw, just log to console
        // This prevents UI errors if backend is down
        console.debug('Backend log storage unavailable:', error);
    }
};

export const NotificationLogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const logIdCounter = useRef(0);

    const isCriticalLog = useCallback((level: LogLevel, category: LogCategory, message: string) => {
        // Always keep warning/error entries
        if (level === 'warning' || level === 'error') {
            return true;
        }

        // Keep explicit socket connection success so users can see connect/disconnect lifecycle
        if (level === 'info' && category === 'SOCKET') {
            const socketMessage = message.toLowerCase();
            if (socketMessage.includes('connected successfully') || socketMessage.includes('connection established')) {
                return true;
            }
        }

        // Allow only issue-related info logs (token/auth/socket/tooling failures)
        if (level === 'info') {
            const text = `${category} ${message}`.toLowerCase();
            const criticalKeywords = [
                'token',
                'auth',
                'unauthorized',
                'expired',
                'invalid',
                'failed',
                'failure',
                'error',
                'disconnect',
                'timeout',
                'forbidden',
                'denied',
                'exception',
            ];

            return criticalKeywords.some((keyword) => text.includes(keyword));
        }

        // Ignore success noise by default
        return false;
    }, []);

    // Cleanup old logs periodically
    const cleanupOldLogs = useCallback((currentLogs: LogEntry[]) => {
        const now = Date.now();
        return currentLogs.filter(log => {
            const logAge = now - log.timestamp.getTime();
            return logAge < LOG_RETENTION_MS;
        });
    }, []);

    const addLog = useCallback((
        level: LogLevel,
        category: LogCategory,
        message: string,
        details?: any,
        metadata?: any
    ) => {
        const newLog: LogEntry = {
            id: `log_${Date.now()}_${logIdCounter.current++}`,
            timestamp: new Date(),
            level,
            category,
            message,
            details,
            metadata
        };

        setLogs(prevLogs => {
            // Add new log
            let updatedLogs = [newLog, ...prevLogs];

            // Cleanup old logs
            updatedLogs = cleanupOldLogs(updatedLogs);

            // Limit total number of logs
            if (updatedLogs.length > MAX_LOGS) {
                updatedLogs = updatedLogs.slice(0, MAX_LOGS);
            }

            return updatedLogs;
        });

        // Increment unread count
        setUnreadCount(prev => prev + 1);

        // TEMPORARILY DISABLED: Send log to backend for file storage (non-blocking)
        // Uncomment when testing performance impact
        // sendLogToBackend(newLog).catch(error => {
        //     console.error('Failed to save log to backend file storage:', error);
        // });

        // Also log to console for debugging
        const consoleMethod = level === 'error' ? console.error : level === 'warning' ? console.warn : console.log;
        consoleMethod(`[${category}] ${message}`, details || '');
    }, [cleanupOldLogs]);

    const clearLogs = useCallback(() => {
        setLogs([]);
        setUnreadCount(0);
    }, []);

    const getLogsByCategory = useCallback((category: LogCategory) => {
        return logs.filter(log => log.category === category);
    }, [logs]);

    const getLogsByLevel = useCallback((level: LogLevel) => {
        return logs.filter(log => log.level === level);
    }, [logs]);

    const markAsRead = useCallback(() => {
        setUnreadCount(0);
    }, []);

    // Connect logger to notification system
    useEffect(() => {
        // Register callback with logger to receive all logs
        logger.setNotificationCallback((level, category, message, details) => {
            const typedLevel = level as LogLevel;
            const typedCategory = category as LogCategory;

            if (!isCriticalLog(typedLevel, typedCategory, message)) {
                return;
            }

            addLog(typedLevel, typedCategory, message, details);
        });

        // Cleanup on unmount
        return () => {
            logger.setNotificationCallback(null);
        };
    }, [addLog, isCriticalLog]);

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo<NotificationLogContextType>(() => ({
        logs,
        addLog,
        clearLogs,
        getLogsByCategory,
        getLogsByLevel,
        unreadCount,
        markAsRead
    }), [logs, addLog, clearLogs, getLogsByCategory, getLogsByLevel, unreadCount, markAsRead]);

    return (
        <NotificationLogContext.Provider value={value}>
            {children}
        </NotificationLogContext.Provider>
    );
};

export const useNotificationLog = () => {
    const context = useContext(NotificationLogContext);
    if (!context) {
        throw new Error('useNotificationLog must be used within NotificationLogProvider');
    }
    return context;
};
